import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Trash2, ChevronDown, ChevronUp } from "lucide-react";
import { useState } from "react";
import AddBlockDropdown from "./add-block-dropdown";
import Block from "./block";
import type { BlockTree } from "@/blocks/block-tree";
import type { RouteBlock } from "@/blocks/routes.blocks";
import type { FeatureBlock } from "@/blocks/geojson.blocks";
import { useRoom } from "@/hooks/use-room";

export default function Route({
  block,
  tree,
}: {
  block: RouteBlock;
  tree: BlockTree;
}) {
  const { docs } = useRoom();
  const [isCollapsed, setIsCollapsed] = useState(false);

  const featureIds = block.content || [];
  const featureBlocks = featureIds
    .map((id) => tree.document.blocks?.find((b) => b.id === id))
    .filter((b): b is FeatureBlock => b?.type === "feature");

  const handleAddFeature = () => {
    if (!docs) return;

    docs.appendBlockToBlock(
      {
        ...block,
        deleted: block.deleted ?? false,
      },
      {
        type: "feature",
        attributes: {
          properties: {
            "map::label": String.fromCharCode(65 + featureBlocks.length),
          },
        },
        content: [],
        documentId: block.documentId,
        deleted: false,
      } satisfies Omit<FeatureBlock, "id">,
    );
  };

  const handleRemoveFeature = (featureId: string) => {
    if (!docs) return;

    docs.removeBlockFromBlock(
      {
        ...block,
        deleted: block.deleted ?? false,
      },
      featureId,
    );
  };

  const handleMoveFeature = (featureId: string, direction: "up" | "down") => {
    if (!docs) return;

    const currentIndex = featureIds.indexOf(featureId);
    if (currentIndex === -1) return;

    const newIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= featureIds.length) return;

    // Create new content array with reordered items
    const newContent = [...featureIds];
    const [movedItem] = newContent.splice(currentIndex, 1);
    if (movedItem) {
      newContent.splice(newIndex, 0, movedItem);
    }

    // Update the route block's content
    docs.updateBlockContent(block.id, newContent);
  };

  const handleModeChange = (mode: string) => {
    if (!docs) return;

    docs.updateBlockAttributes(block.id, {
      ...block.attributes,
      mode: mode as "unspecified" | "bus" | "train" | "walk" | "bike",
    });
  };

  const routeModes = [
    { value: "unspecified", label: "Unspecified" },
    { value: "bus", label: "Bus" },
    { value: "train", label: "Train" },
    { value: "walk", label: "Walk" },
    { value: "bike", label: "Bike" },
  ];

  // Get waypoint labels for summary view
  const waypointLabels = featureBlocks
    .map((fb) => fb.attributes?.properties?.["map::label"])
    .filter(
      (label): label is string => typeof label === "string" && label.length > 0,
    );

  return (
    <div className="border rounded-lg p-4 space-y-3 bg-blue-50/50">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="h-6 w-6 p-0"
          >
            {isCollapsed ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronUp className="h-4 w-4" />
            )}
          </Button>
          <span className="font-medium text-blue-900">Route</span>
          {isCollapsed && (
            <span className="text-sm text-blue-700">
              {block.attributes.mode && block.attributes.mode !== "unspecified"
                ? `${block.attributes.mode} `
                : ""}
              ({featureBlocks.length} waypoint
              {featureBlocks.length !== 1 ? "s" : ""}
              {waypointLabels.length > 0
                ? `: ${waypointLabels.join(", ")}`
                : ""}
              )
            </span>
          )}
        </div>
        {!isCollapsed && (
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-blue-800">Mode</span>
            <Select
              value={block.attributes.mode}
              onValueChange={handleModeChange}
            >
              <SelectTrigger className="w-32">
                <SelectValue placeholder="Mode" />
              </SelectTrigger>
              <SelectContent>
                {routeModes.map((mode) => (
                  <SelectItem key={mode.value} value={mode.value}>
                    {mode.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {!isCollapsed && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-blue-800">Features</span>
            <AddBlockDropdown
              onAddBlock={handleAddFeature}
              allowedTypes={["feature"]}
              label="Add Waypoint"
              size="sm"
              variant="outline"
            />
          </div>

          {featureBlocks.length > 0 ? (
            <div className="space-y-2">
              {featureBlocks.map((featureBlock, index) => (
                <div
                  key={featureBlock.id}
                  className="flex items-start gap-2 p-2 bg-white rounded border"
                >
                  <div className="flex items-center justify-center w-6 h-6 bg-blue-100 text-blue-800 text-xs font-medium rounded-full flex-shrink-0 mt-1">
                    {index + 1}
                  </div>
                  <div className="flex-1">
                    <Block block={featureBlock} tree={tree} />
                  </div>
                  <div className="flex flex-col gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleMoveFeature(featureBlock.id, "up")}
                      disabled={index === 0}
                      className="h-6 w-6 p-0"
                    >
                      ↑
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleMoveFeature(featureBlock.id, "down")}
                      disabled={index === featureBlocks.length - 1}
                      className="h-6 w-6 p-0"
                    >
                      ↓
                    </Button>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveFeature(featureBlock.id)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center text-muted-foreground py-4 text-xs">
              No features added to route
            </div>
          )}
        </div>
      )}
    </div>
  );
}
